/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {
  createStyles,
  withStyles
} from '@material-ui/core/styles';
import MaterialTab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import classnames from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import {getViewDeviceTabs} from '@ion/applications/src/pages/devices/helpers';
import {
  setDevicesTabs,
  setSelectedDevicesTab
} from '@ion/applications/src/pages/devices/state';
import useBulkViewDeviceTabs from '@ion/applications/src/pages/devices/useBulkViewDeviceTabs';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import CopyButton from '@ion/dls/src/components/buttons/CopyButton';
import useExtendStyles from '../hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';
import {IconSize} from '../types/types';

interface DeviceViewDialogTabViewProps {
  device?: Device;
  title?: GenericObject;
  hasSecondaryDevice?: boolean;
  copyValuesToClipboard?: () => void;
};

const style = {
  textStyle : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '18px',
    letterSpacing : '1px'
  }
};

const useStyles = () => {
  return {
    root : {
      flexGrow : 1,
      color    : dlsColor('black'),
      minWidth : '200px'
    },
    endAdornment : {
      position : 'absolute',
      right    : 0
    },
    customTabIndicator : {
      backgroundColor : dlsColor('black')
    },
    tabRoot : {
    },
    tabContainer : {
    },
    formTitle : {
      display        : 'flex',
      justifyContent : 'left',
      alignItems     : 'flex-start',
      width          : '90%',
      position       : 'relative',
      left           : '35px'
    },
    labelContent : {
      ...style,
      color : 'rgba(130, 130, 130, 1)'
    },
    valueContent : {
      ...style,
      color : 'rgba(0, 0, 0, 1)'
    },
    iconContent : {
      position : 'relative',
      top      : '4px'
    },
    closeIcon : {
      position : 'relative',
      top      : '6px',
      left     : '4px'
    }
  };
};

const TabStyle = withStyles(() =>
  createStyles({
    root : {
      '&$selected' : {
        color : dlsColor('black')
      }
    },
    selected : {},
    wrapper  : {
      textOverflow : 'ellipsis',
      whiteSpace   : 'nowrap',
      overflow     : 'hidden',
      display      : 'block',
      padding      : '0 12px'
    }
  })
)(MaterialTab);

/**
 * Renders a tabbed interface for viewing one or more device details within
 * a dialog. Supports tab switching, and copying tab values to the clipboard.
 * Designed for use in device management dialogs or modals.
 *
 * ```
 * 
 * <DeviceViewDialogTabView
 *   device={deviceObject}
 *   title={{ label: "Device" }}
 *   copyValuesToClipboard={handleCopy}
 *   hasSecondaryDevice={false}
 * />
 * 
 * ```
 * 
 */
const DeviceViewDialogTabView = ({
  device,
  title,
  copyValuesToClipboard,
  hasSecondaryDevice = false
}: DeviceViewDialogTabViewProps) => {
  const tabViewStyles = useExtendStyles(useStyles);
  const classes       = tabViewStyles();
  const dispatch      = useDispatch();

  const {
    tabItems,
    selectedIndex,
    handleOnChange,
    onCloseTab
  } = useBulkViewDeviceTabs();

  useEffect(() => {
    if (device && !hasSecondaryDevice) {
      const displayDevices = cloneDeep([device]);
      const items          = getViewDeviceTabs(displayDevices);
      const selectedTab    = displayDevices?.[0];

      dispatch(setDevicesTabs(items, selectedTab));
      dispatch(setSelectedDevicesTab(selectedTab));
    }
  }, [device, hasSecondaryDevice]);

  const onIconClick = useCallback(function(e, index) {
    e?.preventDefault();
    e?.stopPropagation();
    onCloseTab(e, index);
  }, [onCloseTab]);

  const onChangeIndex = useCallback(function(e, index) {
    handleOnChange(e, index);
  }, [handleOnChange]);

  return (
    <div className={classes.formTitle}>
      <Tabs value={selectedIndex}
        onChange={handleOnChange}
        classes={{
          indicator     : classes.customTabIndicator,
          flexContainer : classes.tabContainer,
          root          : classes.tabRoot,
          scroller      : classes?.scroller
        }}
        variant={'scrollable'}
        centered={false}>
        {tabItems?.map((tab, index) => {
          return (
            <TabStyle className={
              classnames(
                classes.root
              )}
            key={`${index}-${tab.tabHeader}`}
            onClick={function(e) { onChangeIndex(e, index); }}
            style={{
              paddingLeft : '32px'
            }}
            label={(
              <>
                <span className={classes.labelContent}>
                  {title?.label}
                </span>
                <span className={classes.valueContent}>
                  {tab?.tabHeader}
                </span>
                <Render.If condition={index === 0}>
                  <span className={classes.iconContent}>
                    <CopyButton size={IconSize.medium} onCopy={copyValuesToClipboard} />
                  </span>
                </Render.If>
                <Render.If condition={index === 1}>
                  <span className={classes.closeIcon} onClick={function(e) { onIconClick(e, index); }}>
                    <MaterialExitCloseIcon size={IconSize.smallMedium}
                      color={dlsColor('grey3')} />
                  </span>
                </Render.If>
              </>
            )} />
          );
        })}
      </Tabs>
    </div>
  );
};

export default React.memo(DeviceViewDialogTabView);
